import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import LegalDocumentCard from '../../components/legal/LegalDocumentCard';
import LegalDocumentModal from '../../components/legal/LegalDocumentModal';
import nexoraLogo from '../../assets/nexora-incon.png';
import {
  codeOfConduct,
  manifesto,
  privacyPolicy,
  subscriptionPolicy,
  termsOfUse,
} from '../../legal/documents';
import type { LegalDocument } from '../../legal/types/legalDocument';
import { auth } from '../../config/firebase';
import { saveLegalAcceptance } from '../../utils/firebaseService';

const legalDocuments = [
  {
    title: 'Manifesto',
    description: 'Conheça os princípios e valores que orientam a Nexora.',
    document: manifesto,
  },
  {
    title: 'Código de Conduta',
    description:
      'Entenda os comportamentos esperados para uma comunidade segura e respeitosa.',
    document: codeOfConduct,
  },
  {
    title: 'Termos de Uso',
    description: 'Regras para utilização dos recursos e serviços da plataforma.',
    document: termsOfUse,
  },
  {
    title: 'Política de Privacidade',
    description: 'Saiba como protegemos, utilizamos e armazenamos seus dados.',
    document: privacyPolicy,
  },
  {
    title: 'Política de Assinaturas',
    description: 'Conheça as regras sobre planos, pagamentos e cancelamentos.',
    document: subscriptionPolicy,
  },
];

export default function LegalConsentPage() {
  const navigate = useNavigate();
  const [acceptedDocuments, setAcceptedDocuments] = useState(
    Object.fromEntries(
      legalDocuments.map((document) => [document.title, false])
    ) as Record<string, boolean>
  );
  const [selectedDocument, setSelectedDocument] =
    useState<LegalDocument | null>(null);
  const [isSavingLegalAcceptance, setIsSavingLegalAcceptance] =
    useState(false);

  const allDocumentsAccepted = legalDocuments.every(
    (document) => acceptedDocuments[document.title]
  );

  function toggleDocument(documentTitle: string) {
    setAcceptedDocuments((currentDocuments) => ({
      ...currentDocuments,
      [documentTitle]: !currentDocuments[documentTitle],
    }));
  }

  function openDocument(document: LegalDocument) {
    setSelectedDocument(document);
  }

  function closeDocument() {
    setSelectedDocument(null);
  }

  async function handleContinue() {
    if (isSavingLegalAcceptance) {
      return;
    }

    const allDocumentsStillAccepted = legalDocuments.every(
      (document) => acceptedDocuments[document.title]
    );

    if (!allDocumentsStillAccepted) {
      return;
    }

    setIsSavingLegalAcceptance(true);

    try {
      const uid = auth.currentUser?.uid;

      if (!uid) {
        return;
      }

      await saveLegalAcceptance(uid);
      navigate('/');
    } catch (error) {
      console.error('Erro ao persistir aceite jurÃ­dico:', error);
    } finally {
      setIsSavingLegalAcceptance(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#020817] px-6 py-12">
      <div className="absolute left-[-220px] top-1/2 h-[500px] w-[500px] -translate-y-1/2 rounded-full bg-violet-500/12 blur-[180px]" />

      <div className="absolute right-[-220px] top-1/2 h-[500px] w-[500px] -translate-y-1/2 rounded-full bg-violet-500/12 blur-[180px]" />

      <div className="absolute left-9 top-9 opacity-60">
        <img
          src={nexoraLogo}
          alt="Nexora"
          className="h-10 w-10 object-contain"
        />
      </div>

      <div className="absolute left-1/2 top-[70px] h-[2px] w-[140px] -translate-x-1/2 bg-gradient-to-r from-transparent via-violet-500 to-transparent blur-sm opacity-30" />

      <motion.main
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-xl text-center"
      >
        <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-3xl border border-violet-500/30 bg-[#0f172a]/80 shadow-[0_0_40px_rgba(255,255,255,0.03)] backdrop-blur-xl">
          <img
            src={nexoraLogo}
            alt="Nexora"
            className="h-14 w-14 object-contain"
          />
        </div>

        <h1 className="text-5xl font-bold tracking-tight text-white">
          Estamos quase lá
        </h1>

        <div className="mx-auto mb-6 mt-6 h-1 w-20 rounded-full bg-violet-500" />

        <p className="mx-auto max-w-[420px] text-[15px] leading-6 text-slate-300">
          Antes de acessar a plataforma, confirme que você está de acordo com
          os documentos institucionais da Nexora.
        </p>

        <div className="mx-auto mt-8 space-y-3 text-left">
          {legalDocuments.map((document) => (
            <LegalDocumentCard
              key={document.title}
              title={document.title}
              description={document.description}
              checked={acceptedDocuments[document.title]}
              onToggle={() => toggleDocument(document.title)}
              onRead={() => openDocument(document.document)}
            />
          ))}
        </div>

        <button
          type="button"
          disabled={!allDocumentsAccepted || isSavingLegalAcceptance}
          onClick={handleContinue}
          className={`mt-8 inline-flex h-14 items-center justify-center rounded-2xl px-8 font-semibold transition-all duration-300 ${
            allDocumentsAccepted && !isSavingLegalAcceptance
              ? 'cursor-pointer bg-gradient-to-r from-violet-600 to-purple-500 text-white shadow-[0_0_40px_rgba(139,92,246,0.35)] hover:scale-[1.03] hover:shadow-[0_0_60px_rgba(139,92,246,0.55)]'
              : 'cursor-not-allowed bg-gradient-to-r from-violet-600/40 to-purple-500/40 text-white/45 shadow-[0_0_28px_rgba(139,92,246,0.18)]'
          }`}
        >
          Continuar
        </button>
      </motion.main>

      {selectedDocument && (
        <LegalDocumentModal
          document={selectedDocument}
          isOpen={Boolean(selectedDocument)}
          onClose={closeDocument}
        />
      )}
    </div>
  );
}
